import { Input } from '@headlessui/react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

function Editable(props: {
	placeholder: string;
	emptyValue: string;
	value: string;
	onChange: (value: string) => void;

	valueButtonClassName?: string;
	className?: string;
	inputClassName?: string;
}) {
	const {
		value,
		onChange,
		placeholder,
		emptyValue,
		valueButtonClassName,
		className,
		inputClassName,
	} = props;
	const [isEditing, setIsEditing] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const originalValueRef = useRef(value);
	const instructionsId = useId();

	// Auto-focus and select text when entering edit mode
	useLayoutEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditing]);

	// Handle keyboard appearance on mobile - only scroll if out of view
	useEffect(() => {
		if (!isEditing || !inputRef.current) {
			return;
		}

		const checkAndScroll = () => {
			if (!inputRef.current) {
				return;
			}

			const element = inputRef.current;
			const rect = element.getBoundingClientRect();
			const isInViewport =
				rect.top >= 0 &&
				rect.left >= 0 &&
				rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
				rect.right <= (window.innerWidth || document.documentElement.clientWidth);

			// Only scroll if element is not fully visible
			if (!isInViewport) {
				element.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest',
				});
			}
		};

		// Check after a short delay to allow keyboard to appear
		const timeoutId = setTimeout(checkAndScroll, 150);

		const handleResize = () => {
			// Check again when viewport changes (keyboard appearing)
			setTimeout(checkAndScroll, 150);
		};

		// Listen for viewport changes (keyboard appearing/disappearing)
		window.addEventListener('resize', handleResize);

		// Also listen for visual viewport changes (more accurate for mobile keyboards)
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', handleResize);
		}

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', handleResize);
			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', handleResize);
			}
		};
	}, [isEditing]);

	const handleStartEditing = useCallback(() => {
		setIsEditing(true);
	}, []);

	const handleSave = useCallback(() => {
		originalValueRef.current = value;
		onChange(value);
		setIsEditing(false);
	}, [value, onChange]);

	const handleCancel = useCallback(() => {
		onChange(originalValueRef.current);
		setIsEditing(false);
	}, [onChange]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleSave();
			} else if (e.key === 'Escape') {
				e.preventDefault();
				handleCancel();
			}
		},
		[handleSave, handleCancel],
	);

	const handleBlur = useCallback(
		(_e: React.FocusEvent<HTMLInputElement>) => {
			handleSave();
		},
		[handleSave],
	);

	if (isEditing) {
		return (
			<fieldset
				className={cn('flex h-8 max-w-4/5 items-stretch sm:h-9 sm:max-w-2/3', className)}
				aria-label="Edit text content"
			>
				<Input
					ref={inputRef}
					type="text"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					className={cn(
						'data-focus:-outline-offset-2 block w-full rounded-s border border-gray-300 border-e-0 bg-white px-2 py-1.5 font-medium text-[0.7rem] text-gray-900 focus:not-data-focus:outline-none data-focus:outline-2 data-focus:outline-gray-300 sm:text-[0.8rem]',
						inputClassName,
					)}
					placeholder={placeholder}
					aria-label="Edit text content"
					aria-describedby={instructionsId}
				/>
				<div id={instructionsId} className="sr-only">
					Press Enter to save, or Escape to cancel
				</div>
				<button
					type="button"
					onClick={handleSave}
					className="inline-flex items-center rounded-e border border-gray-300 bg-gray-100 px-2 font-medium text-[0.7rem] text-gray-900 outline-none transition-[color,box-shadow] focus:z-10 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 sm:px-3 sm:text-[0.8rem]"
					aria-label="Save changes"
					title="Save changes (Enter)"
				>
					Save
				</button>
			</fieldset>
		);
	}

	return (
		<div className={className}>
			{value ? (
				<button
					type="button"
					onClick={handleStartEditing}
					className={cn(
						'inline-flex h-8 items-center text-left font-semibold text-[0.7rem] text-gray-900 leading-tight ring-offset-2 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:h-9 sm:text-[0.8rem]',
						valueButtonClassName,
					)}
					aria-label={`Edit text content: ${value}`}
					title="Click to edit text content"
				>
					{value}
				</button>
			) : (
				<button
					type="button"
					onClick={handleStartEditing}
					className="inline-flex h-8 items-center rounded-md border border-gray-300 border-dashed px-2.5 py-1.5 font-medium text-[0.7rem] text-gray-400 transition-colors hover:border-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:h-9 sm:px-3 sm:py-2 sm:text-[0.8rem]"
					aria-label={`Add text content: ${emptyValue}`}
					title="Click to add text content"
				>
					{emptyValue}
				</button>
			)}
		</div>
	);
}

export { Editable };
